import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import cn from 'classnames';
import styles from './Collection.scss';
import noBuildings from 'assets/images/no_photo/no_photo_150px.png';
import arrow from 'assets/images/icons/arrow-blue.svg';

import {
  removeFromCollection,
  addComment,
  loadCollectionBuildingLots,
} from '../../actions/collections';
import { ROOT_URL } from '../../constants/Config';
import tracker from '../../services/tracker';
import { withTranslation } from 'react-i18next';
import { gaEvent, trackGA } from 'services/ga';
import debounce from 'lodash.debounce';
import { formatNumber } from '../../utils';

const formatTimeSpent = (seconds, t) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours} ${t('components.collectionItem.hour')}`);
  if (minutes > 0) parts.push(`${minutes} ${t('components.collectionItem.min')}`);
  if (remainingSeconds > 0) parts.push(`${remainingSeconds} ${t('components.collectionItem.sec')}`);

  return parts.join(' ');
};

const StatsInfoField = ({ title, text }) => (
  <div className={styles.StatsInfoField}>
    <div className={styles.StatsInfoFieldTitle}>{title}</div>
    <div className={styles.SendCollectionBtnText}>{text}</div>
  </div>
);

const BuildingStats = ({ stats, t }) => {
  if (!stats) return null;

  return (
    <div className={styles.Stats}>
      <StatsInfoField title={t('components.collectionItem.views')} text={stats.views || '-'} />
      <StatsInfoField
        title={t('components.collectionItem.timeSpent')}
        text={formatTimeSpent(stats.time_spent, t)}
      />
      <StatsInfoField
        title={t('components.collectionItem.photoClicks')}
        text={stats.photo_clicks || '-'}
      />
      <StatsInfoField
        title={t('components.collectionItem.mapClicks')}
        text={stats.map_clicks || '-'}
      />
      <StatsInfoField
        title={t('components.collectionItem.phoneClicks')}
        text={stats.phone_clicks || '-'}
      />
    </div>
  );
};

const LotItem = ({ lot, t }) => (
  <div className={styles.LotItem}>
    <div className={styles.LotMainInfo}>
      <div className={styles.LotMainTop}>
        <div className={styles.LotArea}>{lot.area} м²</div>
        <div className={styles.LotNumber}>
          {t('components.collectionItem.LOT')} {lot.number}
        </div>
      </div>
      <div className={styles.LotMainBottom}>
        <div className={styles.LotMainBottomDot} />
        <div>
          {t('components.collectionItem.corpus')} {lot.house_body.name},{' '}
          {t('components.collectionItem.section')} {lot.house_body.section.name}
        </div>
      </div>
    </div>
    <div className={styles.LotRow}>
      {lot.row} {t('components.collectionItem.floor')}
    </div>
    <div className={styles.LotPrice}>{formatNumber(lot.price)} ₽</div>
    <div className={styles.LotClicks}>
      <div className={styles.LotClicksTitle}>{t('components.collectionItem.lotClicks')}</div>
      <div className={styles.LotClicksValue}>{lot.views}</div>
    </div>
    <div className={styles.LotClicks}>
      <div className={styles.LotClicksTitle}>{t('components.collectionItem.likeClicks')}</div>
      <div className={styles.LotClicksValue}>{lot.likes}</div>
    </div>
  </div>
);

class Collection extends Component {
  constructor(props) {
    super(props);

    this.state = {
      editing: false,
      comment: props.building.comment,
      lotsStats: [],
      showLots: false,
      lotsOffset: 0,
      lotsHasMore: true,
      lotsLoading: false,
    };

    this.debouncedTrackEventInput = debounce(
      () =>
        this.handleTrackEvent({
          eventName: 'collectionListItemCommentInput',
          eventLabel: 'dobavit_kommentarii',
        }),
      3000
    );
  }

  componentDidMount() {
    this.fetchLotsStats();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.sessionId !== this.props.sessionId ||
      prevProps.building.id !== this.props.building.id
    ) {
      this.resetLots();
      this.fetchLotsStats(false, 0);
    }
  }

  handleChange = (e) => {
    if (e.target.value.length > 2) {
      this.debouncedTrackEventInput();
    }
    this.setState({
      comment: e.target.value,
    });
  };

  getInputRef = (ref) => {
    this.input = ref;
  };

  handleEditClick = () => {
    tracker.sendEvent('Web_Selections_Edit_Comment');
    this.setState({
      editing: true,
      comment: this.props.building.comment,
    });
    this.input.focus();
  };

  handleCancleClick = () => {
    this.handleTrackEvent({
      eventName: 'collectionListItemCommentButtonsClick',
      eventLabel: 'otmenit',
    });

    this.setState({
      editing: false,
      comment: this.props.building.comment,
    });
  };

  handleCommentClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    tracker.sendEvent('Web_Selections_Add_Comment');
    this.setState({
      editing: !this.state.editing,
    });
    this.input.focus();
  };

  handleCommentSubmit = (e) => {
    e.preventDefault();
    const { addComment, referal, building } = this.props;
    addComment({
      referal,
      buildingId: building.id,
      comment: this.state.comment,
    });
    this.setState({
      editing: false,
    });
  };

  handleRemoveClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const { removeFromCollection, referal, building, collectionName, profile } = this.props;
    tracker.sendEvent('Web_Selections_Delete_Comment', {
      id: building.id,
      name: building.title,
    });

    this.handleTrackEvent({
      eventName: 'collectionListItemButtonsClick',
      eventLabel: 'udalit',
    });

    removeFromCollection(
      {
        referal,
        buildingId: building.id,
      },
      {
        collectionName,
        buildingName: building.title,
        data: building,
        profileId: profile.id,
      }
    );
  };

  handleTrackEvent = ({ eventName, eventLabel }) => {
    const { profile, collectionName, building } = this.props;
    trackGA(
      gaEvent[eventName]({
        eventLabel,
        eventContext: collectionName,
        productType: building.type_id === 1 ? 'zhk' : 'kp_i_th',
        productName: building.title,
        productID: building.id,
        user_id: profile.id,
      })
    );
  };

  fetchLotsStats = async (append = false, offset = this.state.lotsOffset) => {
    const { building } = this.props;
    const { lotsStats } = this.state;

    if (this.state.lotsLoading) return;

    this.setState({ lotsLoading: true });

    try {
      const response = await this.props.loadCollectionBuildingLots({
        building_id: building.id,
        session_id: this.props.sessionId || null,
        limit: 10,
        offset,
      });

      if (response && response.data && response.data.lots) {
        const newLots = response.data.lots;
        this.setState({
          lotsStats: append ? [...lotsStats, ...newLots] : newLots,
          lotsOffset: offset + newLots.length,
          lotsHasMore: newLots.length === 10,
          lotsLoading: false,
        });
      } else {
        this.setState({ lotsHasMore: false, lotsLoading: false });
      }
    } catch (error) {
      console.error('Ошибка при загрузке лотов:', error);
      this.setState({ lotsLoading: false });
    }
  };

  handleLotsScroll = (e) => {
    const { lotsHasMore, lotsLoading } = this.state;
    const bottomReached = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;

    if (bottomReached && lotsHasMore && !lotsLoading) {
      this.fetchLotsStats(true);
    }
  };

  getCurrentSessionStats = () => {
    const { building, sessionId } = this.props;

    if (!building.sessions) {
      return null;
    }

    if (!sessionId || sessionId === 'total') {
      return building.sessions.total || null;
    }

    return (
      (building.sessions.items &&
        building.sessions.items.find((item) => item.session_uuid === sessionId)) ||
      null
    );
  };

  prepareStatsForBuilding = () => {
    const { sessionId } = this.props;
    const stats = this.getCurrentSessionStats();

    if (!stats) {
      return {};
    }

    const isTotalSession = !sessionId || sessionId === 'total';

    return {
      views: isTotalSession ? stats.total_views : stats.views,
      time_spent: isTotalSession ? stats.total_time_spent : stats.time_spent,
      photo_clicks: isTotalSession ? stats.total_photo_clicks : stats.photo_clicks,
      map_clicks: isTotalSession ? stats.total_map_clicks : stats.map_clicks,
      phone_clicks: isTotalSession ? stats.total_phone_clicks : stats.phone_clicks,
    };
  };

  resetLots = () => {
    this.setState({
      lotsOffset: 0,
      lotsStats: [],
      lotsHasMore: true,
      lotsLoading: false,
    });
  };

  toggleShowLots = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState((prevState) => ({
      showLots: !prevState.showLots,
    }));
  };

  render() {
    const { t } = this.props;
    const {
      title,
      address,
      comment,
      image,
      id: buildingId,
      construction_finished: isFinished,
      construction_ending: quater,
      year_built: year,
      status,
    } = this.props.building;
    const { showLots, lotsStats } = this.state;

    const imageId = image && image.id;

    return (
      <div
        className={cn(styles.ItemWrap, {
          [styles.ItemActive]: comment || this.state.comment || this.state.editing,
        })}
      >
        <Link
          to={`/building/${buildingId}`}
          className={styles.Item}
          onClick={() => {
            this.handleTrackEvent({
              eventName: 'collectionListItemButtonsClick',
              eventLabel: 'obyekt',
            });
          }}
        >
          <div className={cn(styles.ItemImage, { [styles.ItemImageNoImage]: !image })}>
            {image ? (
              <img src={`${ROOT_URL}/image?id=${imageId}&size=medium`} alt="" />
            ) : (
              <img src={noBuildings} alt="" />
            )}
          </div>
          <p className={styles.ItemTitle}>
            <span>{title}</span>{' '}
            <span className={styles.LotTag}>
              {t('components.collectionItem.lot', { buildingId })}
            </span>
            {status == 3 ? (
              <span className={cn(styles.ItemStatus, styles.AlmostFinished)}>
                {t('components.collectionItem.almostFinished')}
              </span>
            ) : (
              <span className={cn(styles.ItemStatus, { [styles.Finished]: isFinished })}>
                {isFinished ? t('components.collectionItem.finished') : `${quater} / ${year || ''}`}
              </span>
            )}
          </p>
          <p className={styles.ItemAddress}>
            <span>{address}</span>
          </p>
          <div className={styles.ItemCommentButton} onClick={this.handleCommentClick}>
            <span
              className={styles.TabletHidden}
              onClick={() => {
                this.handleTrackEvent({
                  eventName: 'collectionListItemButtonsClick',
                  eventLabel: 'dobavit_kommentarii',
                });
              }}
            >
              {t('components.collectionItem.addComment')}
            </span>
            <span className={styles.TabletOnly}>{t('components.collectionItem.comment')}</span>
          </div>
          <div className={styles.ItemDelete} onClick={this.handleRemoveClick}>
            {t('components.collectionItem.delete')}
          </div>
          <BuildingStats stats={this.prepareStatsForBuilding()} t={t} />
          {lotsStats.length ? (
            <div className={styles.LotsList}>
              <div className={styles.LotsTitle} onClick={this.toggleShowLots}>
                <span>{t('components.collectionItem.showLots')}</span>
                <img src={arrow} alt="arrow" className={showLots && styles.Arrow} />
              </div>
              {showLots && (
                <div
                  className={styles.LotList}
                  style={{ maxHeight: '400px', overflowY: 'auto' }}
                  onScroll={this.handleLotsScroll}
                >
                  {lotsStats.map((lot) => (
                    <LotItem key={lot.id} lot={lot} t={t} />
                  ))}
                  {this.state.lotsLoading && <div className={styles.LotsLoading}>Загрузка...</div>}
                </div>
              )}
            </div>
          ) : null}
        </Link>

        <form className={styles.ItemComment} onSubmit={this.handleCommentSubmit}>
          <input
            placeholder={this.state.editing ? t('components.collectionItem.writeComment') : ''}
            value={this.state.comment || ''}
            className={styles.CommentInput}
            onChange={this.handleChange}
            ref={this.getInputRef}
          />
          <div className={styles.CommentButtons}>
            {this.state.editing ? (
              <div className={styles.CommentButton} onClick={this.handleCancleClick}>
                {t('components.collectionItem.cancel')}
              </div>
            ) : (
              <div className={styles.CommentButton} onClick={this.handleEditClick}>
                {t('components.collectionItem.edit')}
              </div>
            )}
            {this.state.editing && this.state.comment !== comment && (
              <button
                className={styles.CommentButton}
                onClick={() => {
                  this.handleTrackEvent({
                    eventName: 'collectionListItemCommentButtonsClick',
                    eventLabel: 'sohranit',
                  });
                }}
              >
                {t('components.collectionItem.save')}
              </button>
            )}
          </div>
        </form>
      </div>
    );
  }
}

export default connect(
  (state) => ({
    referal: state.collection.selection.referal,
    collectionName: state.collection.selection.name,
    profile: state.profile,
  }),
  { removeFromCollection, addComment, loadCollectionBuildingLots }
)(withTranslation()(Collection));



// WEBPACK FOOTER //
// ./src/components/CollectionItem/Collection.js